import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom"; // 🚀 1. Thêm hook useSearchParams
import { BarChart3 } from "lucide-react";
import RevenueLineChart from './components/RevenueLineChart';
import SearchForm from '../../../components/common/SearchForm';
import RevenueTable from './components/RevenueTable';
import NoDataState from '../../../components/common/NoDataState';
import LoadingState from '../../../components/common/LoadingState';
import MerchantOverview from './components/MerchantOverview';
import { merchantService } from '../../../services/apiService';
import { useDispatch, useSelector } from "react-redux";
import { setMerchantId } from "../../../features/global/globalSlice";

export default function Revenue() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams(); // 🚀 2. Khai báo hook lấy query params từ URL
  const terminalIdParam = searchParams.get("terminalId"); // Lấy giá trị của ?terminalId=...

  const currentMerchantId = useSelector((state) => state.global.merchant_id);
  const [searchCode, setSearchCode] = useState(terminalIdParam || currentMerchantId || "");
  
  // Lấy tháng hiện tại và định dạng thành 'YYYY-MM' (ISO format)
  const currentMonth = new Date().toISOString().slice(0, 7); // 'YYYY-MM'
  const [selectedMonth, setSelectedMonth] = useState(currentMonth); 
  const [revenueLog, setRevenueLog] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState();
  const [hasSearched, setHasSearched] = useState(false);
  const [merchantInfo, setMerchantInfo] = useState();

  const fetchRevenueData = async (storeCode) => {
    try {
      const response = await merchantService.getDailyRevenue(
        storeCode,
        selectedMonth,
      );

      const data = (response?.data?.daily || []).sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );

      setSummary(response?.data?.summary);
      
      // Gộp dữ liệu theo ngày
      const aggregatedData = data.reduce((acc, item) => {
        const date = item.date;

        if (!acc[date]) {
          acc[date] = {
            date,
            revenue: item.total_revenue,
            tx_count: item.tx_count,
          };
        } else {
          acc[date].revenue += item.total_revenue;
          acc[date].tx_count += item.tx_count;
        }

        return acc;
      }, {});

      // Convert object → array + tính % thay đổi
      const result = Object.values(aggregatedData).map(
        (item, index, array) => ({
          date: item.date,
          revenue: item.revenue * 1000, // VND
          tx_count: item.tx_count,
          change:
            index === array.length - 1
              ? "0.0"
              : ((item.revenue / array[index + 1]?.revenue - 1) * 100).toFixed(
                  1,
                ),
        }),
      );
      return result;
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      return [];
    }
  };

  const fetchMerchantInfo = async (terminalIDs) => {
    try {
      const response = await merchantService.getInfo(terminalIDs);

      const apiResponse = response.data;
      return apiResponse?.metadata || [];
    } catch (error) {
      console.error("Error fetching merchant info:", error);
      return [];
    }
  };

  // Hàm xử lý logic nạp dữ liệu
  const loadData = async (codes) => {
    if (!codes || (Array.isArray(codes) && codes.length === 0)) return;

    const codeList = Array.isArray(codes) ? codes : [codes];

    setIsLoading(true);
    setHasSearched(true);

    try {
      const revenueData = await fetchRevenueData(codes).catch(e => {
        console.warn("Lỗi fetchRevenueData:", e.message);
        return [];
      });
      const merchantList = await fetchMerchantInfo(codeList).catch(e => {
        console.warn("Lỗi fetchMerchantInfo:", e.message);
        return [];
      });
      
      setRevenueLog(revenueData);
      setMerchantInfo(merchantList?.[0]);
    } catch (error) {
      console.error("Lỗi khi tải dữ liệu:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🚀 3. Cập nhật useEffect để ưu tiên đọc terminalId từ URL query param
  useEffect(() => {
    // Ưu tiên lấy từ tham số URL trước, nếu không có mới lấy từ Redux
    const targetMerchantId = terminalIdParam || currentMerchantId;

    if (targetMerchantId) {
      setSearchCode(targetMerchantId);

      // Nếu URL có terminalId khác với Redux store thì đồng bộ lại Redux
      if (terminalIdParam && terminalIdParam !== currentMerchantId) {
        dispatch(setMerchantId(terminalIdParam));
      }

      loadData(targetMerchantId);
    } else {
      setSearchCode("");
      setRevenueLog([]);
      setSummary(undefined);
      setMerchantInfo(undefined);
      setHasSearched(false);
    }
  }, [terminalIdParam, currentMerchantId, selectedMonth]);

  const handleSearch = (e, nextMerchantId = searchCode) => {
    if (e) e.preventDefault();
    const normalizedMerchantId = nextMerchantId.trim();

    if (!normalizedMerchantId) return;

    if (normalizedMerchantId !== currentMerchantId) {
      dispatch(setMerchantId(normalizedMerchantId));
      return;
    }

    loadData(normalizedMerchantId);
  };

  const totals = useMemo(() => {
    const rev = revenueLog.reduce((acc, curr) => acc + curr.revenue, 0);
    const tx = revenueLog.reduce((acc, curr) => acc + (curr.tx_count || 0), 0);
    return { rev, tx };
  }, [revenueLog]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans">
      <main className="max-w-8xl mx-auto px-4 py-8">
        <SearchForm
          searchCode={searchCode}
          setSearchCode={setSearchCode}
          handleSearch={handleSearch}
          isLoading={isLoading}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
        />

        {/* Content Area */}
        {!hasSearched ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-3xl py-20 flex flex-col items-center justify-center text-slate-400">
            <div className="bg-slate-50 p-6 rounded-full mb-4">
              <BarChart3 size={48} className="opacity-20" />
            </div>
            <p className="font-semibold text-slate-500">
              Vui lòng nhập mã cửa hàng để bắt đầu
            </p>
            <p className="text-sm">
              Hệ thống sẽ tổng hợp dữ liệu doanh thu dựa trên mã truy vấn.
            </p>
          </div>
        ) : isLoading ? (
          <LoadingState />
        ) : (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <MerchantOverview
              merchantInfo={merchantInfo}
              revenueLog={revenueLog}
              summary={summary}
            />
            <RevenueLineChart data={revenueLog} />
            <RevenueTable
              merchantInfo={merchantInfo}
              selectedMonth={selectedMonth}
              revenueLog={revenueLog}
              searchCode={currentMerchantId || searchCode}
              totals={totals}
            />
          </div>
        )}
      </main>
    </div>
  );
}